import styles from './AccountCard.module.scss'
import { Button, Card, Icon, Tag } from '@boltenergy-be/design-system'
import { formatAddress } from 'utils/user.ts'
import { deleteRedirect } from 'store/app/slice.ts'
import { routes } from 'types/routes.ts'
import { useStoreDispatch, useStoreSelector } from 'hooks/store.ts'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { setSelectedContract } from 'store/contact/slice.ts'
import { AccountCardProps } from './types.ts'
import { useEffect, useState } from 'react'
import { AccountRecordTypeIds } from 'types/contacts.ts'
import {
  getBillingContractToSelect,
  getLastOpenedContract,
  groupBillingContractsByActiveAddresses,
  setLastOpenedContract
} from 'utils/contracts.ts'
import { UserTypes } from 'store/auth/types.ts'
import { getActiveSocialTariffAttestation } from 'store/contact/utils.ts'
import { useGetCustomerPaymentPlanQuery } from 'store/queries/bolt-backend/customers'
import { log, trackSentryCount } from 'utils/logging.ts'
import { Criticality, Flow, Severity } from 'types/logging.ts'

const AccountCard = ({ account }: AccountCardProps) => {
  // Redux store
  const { redirect } = useStoreSelector((store) => store.app)
  const { userType } = useStoreSelector((store) => store.auth)
  const { billingContracts } = useStoreSelector((store) => store.contact)
  const dispatch = useStoreDispatch()

  // Redux queries
  const { currentData } = useGetCustomerPaymentPlanQuery(
    { customerNumber: account.customerNumber },
    { skip: !account.customerNumber || userType !== UserTypes.SUPER_USER }
  )

  // React Router
  const navigate = useNavigate()

  // i18n
  const { t } = useTranslation(['home', 'common'])

  // Local state
  const [isOpen, setIsOpen] = useState<boolean | null>(account.billingContracts.length > 1 ? false : null)

  // Constants
  const { active, inactive } = groupBillingContractsByActiveAddresses(account.billingContracts, billingContracts)
  const contractToSelect = getBillingContractToSelect(account.billingContracts, billingContracts)
  const showSocialTariffTag = userType === UserTypes.SUPER_USER && !!getActiveSocialTariffAttestation(account.attestations, undefined)
  const showPaymentPlanTag = userType === UserTypes.SUPER_USER && !!currentData?.hasActivePaymentPlan
  const hasTags = showSocialTariffTag || showPaymentPlanTag
  const amountOfTotalBillingContractsForContact = Object.keys(billingContracts).length

  // Log a metric when a customer is attempting to load an account with no billing contracts
  useEffect(() => {
    if (contractToSelect) return

    if (amountOfTotalBillingContractsForContact > 0) {
      trackSentryCount('account_without_billing_contracts', { customerNumber: account.customerNumber })
    } else {
      log({
        error: 'Customer has no visible account cards and no billing contracts',
        identifier: `[${Flow.CONTRACTS}:selectAccountContract]`,
        severity: Severity.WARNING,
        criticality: Criticality.LOW,
        metaData: {
          account: {
            customerNumber: account.customerNumber
          }
        }
      })
    }
  }, [contractToSelect, account.customerNumber, amountOfTotalBillingContractsForContact])

  /**
   * Select contract in store
   * @param {string} contractNumber
   */
  const selectContractInStore = (contractNumber: string) => {
    dispatch(setSelectedContract(contractNumber))
  }

  /**
   * Handles the click on a contract/address
   * @param {string} selectedBillingCustomerNumber
   * @returns void
   */
  const handleSelectContract = (selectedBillingCustomerNumber: string) => {
    setLastOpenedContract(account.customerNumber, selectedBillingCustomerNumber)
    selectContractInStore(selectedBillingCustomerNumber)

    // Define where to redirect: if redirect string in AppStore present, use that. Else redirect to overview
    if (redirect) {
      void navigate(redirect)
      // Clear redirect from AppStore
      dispatch(deleteRedirect())
    } else {
      void navigate(routes.OVERVIEW)
    }
  }

  /**
   * Selects the first active contract or first contract
   */
  const handleClickAccountInfo = () => {
    const lastOpenedContract = getLastOpenedContract(account.customerNumber)

    // The stored contract outlives the contract it points at, so it is only usable while that contract is still in the store
    if (lastOpenedContract && billingContracts[lastOpenedContract]) {
      handleSelectContract(lastOpenedContract)
      return
    }

    // Select first active contract or first contract
    if (!contractToSelect) return

    setLastOpenedContract(account.customerNumber, contractToSelect)
    handleSelectContract(contractToSelect)
  }

  if (account.billingContracts.length === 0 || !contractToSelect) return null

  return (
    <Card as="li" key={account.customerNumber} className={styles['account-card']}>
      <button onClick={handleClickAccountInfo} className={styles.info}>
        <strong>{account.name}</strong>
        <Icon name="chevronRight" className={styles.chevron} />
        <div className={styles['number-and-tags']}>
          <span className="grid-col-full">{account.customerNumber}</span>

          {hasTags && (
            <div className={styles.tags}>
              {showSocialTariffTag && (
                <Tag size="small" type="informative">
                  {t(($) => $.superUserTags.socialTariff, {
                    ns: 'common'
                  })}
                </Tag>
              )}

              {showPaymentPlanTag && (
                <Tag size="small" type="soft">
                  {t(($) => $.superUserTags.paymentPlan, {
                    ns: 'common'
                  })}
                </Tag>
              )}
            </div>
          )}
        </div>

        {account.recordType === AccountRecordTypeIds.PRODUCER && (
          <Tag size="small" className="grid-col-full" type="warning" icon="electricity">
            {t(($) => $.accountSelection.producer)}
          </Tag>
        )}
      </button>

      <hr />

      {account.billingContracts.length === 1 && active.length === 1 ? (
        <Button
          representation="link"
          variant="tertiary"
          onClick={() => handleSelectContract(billingContracts[account.billingContracts[0]].contractNumber)}
          className={styles.address}
          title={formatAddress(billingContracts[account.billingContracts[0]].serviceContracts.electricity.deliveryPoint.address) || ''}
        >
          {formatAddress(billingContracts[account.billingContracts[0]].serviceContracts.electricity.deliveryPoint.address)}
        </Button>
      ) : !isOpen && account.billingContracts.length > 1 ? (
        <Button
          representation="link"
          variant="tertiary"
          onClick={() => setIsOpen(!isOpen)}
          className={styles.toggle}
          trailingIcon="chevronDown"
        >
          {t(($) => $.accountSelection.showMore, {
            count: account.billingContracts.length
          })}
        </Button>
      ) : (
        <>
          {active.length > 0 && (
            <ul className={styles.contracts}>
              {active.map((billingContractNumber) => (
                <li key={billingContractNumber}>
                  <Button
                    representation="link"
                    variant="tertiary"
                    onClick={() => handleSelectContract(billingContractNumber)}
                    className={styles.address}
                    title={formatAddress(billingContracts[billingContractNumber].serviceContracts.electricity.deliveryPoint.address) || ''}
                  >
                    {formatAddress(billingContracts[billingContractNumber].serviceContracts.electricity.deliveryPoint.address)}
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {inactive.length > 0 && (
            <div>
              <small className={styles.divider}>
                {t(($) => $.inactiveAddresses, {
                  ns: 'common'
                })}
              </small>
              <ul className={styles.contracts}>
                {inactive.map((billingContractNumber) => (
                  <li key={billingContractNumber}>
                    <Button
                      representation="link"
                      variant="tertiary"
                      onClick={() => handleSelectContract(billingContractNumber)}
                      className={styles.address}
                      title={
                        formatAddress(billingContracts[billingContractNumber].serviceContracts.electricity.deliveryPoint.address) || ''
                      }
                    >
                      {formatAddress(billingContracts[billingContractNumber].serviceContracts.electricity.deliveryPoint.address)}
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </Card>
  )
}

export default AccountCard
